import { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { PERSISTED_CHART_KIND } from '../lib/statisticsPersistedCharts';
import ProcessDistributionCharts from '../../pipeline/ProcessDistributionCharts';
import OrderPrimaryPhaseDimensionPanel from '../../pipeline/OrderPrimaryPhaseDimensionPanel';
import OrderPrimaryPhaseChinaMap from '../../pipeline/OrderPrimaryPhaseChinaMap';
import DashboardOverdueRankPanel from '../../overdue/DashboardOverdueRankPanel';
import { OVERDUE_CHART_DIMENSION } from '../../overdue/lib/overdueChartConfig';
import useBuiltinChartDisplay from '../hooks/useBuiltinChartDisplay';

const BuiltinLiveChartBody = ({ snapshot, height, fillSlot = false, enableOrderDrill = false, onDrillSelect, frozenBundle = null }) => {
	const { chartKind, isSale, pipeline, dimension, map, overdue } = useBuiltinChartDisplay({ snapshot, frozenBundle });
	const [overdueDimension, setOverdueDimension] = useState(OVERDUE_CHART_DIMENSION.CUSTOMER);
	const drillProps = {
		enableOrderDrill,
		onDrillSelect,
	};

	if (chartKind === PERSISTED_CHART_KIND.PIPELINE_BAR) {
		return (
			<ProcessDistributionCharts
				lensData={pipeline.lensData}
				isLoading={pipeline.isLoading}
				selectedPhase={pipeline.selectedPhase}
				onSelectPhase={pipeline.onSelectPhase}
				animationKey={pipeline.animationKey}
				activeMetric={isSale ? pipeline.barMetric : pipeline.activeMetric}
				barOnly
				dualPhaseMetric={isSale ? pipeline.barDualPhaseMetric : true}
				barHeight={height}
				fillSlot={fillSlot}
				{...drillProps}
			/>
		);
	}

	if (chartKind === PERSISTED_CHART_KIND.PIPELINE_PIE) {
		return (
			<ProcessDistributionCharts
				lensData={pipeline.lensData}
				isLoading={pipeline.isLoading}
				selectedPhase={pipeline.selectedPhase}
				onSelectPhase={pipeline.onSelectPhase}
				animationKey={pipeline.animationKey}
				activeMetric={pipeline.activeMetric}
				pieOnly
				dualPhaseMetric
				pieHeight={height}
				fillSlot={fillSlot}
				{...drillProps}
			/>
		);
	}

	if (chartKind === PERSISTED_CHART_KIND.PIPELINE_DIMENSION) {
		return (
			<OrderPrimaryPhaseDimensionPanel
				rows={dimension.rows}
				isLoading={dimension.isLoading}
				activeDimension={dimension.activeDimension}
				onDimensionChange={dimension.onDimensionChange}
				activeMetric={dimension.activeMetric}
				selectedKey={dimension.selectedKey}
				onSelectRow={dimension.onSelectRow}
				animationKey={dimension.animationKey}
				barHeight={height}
				topN={fillSlot ? 8 : 12}
				showHint={false}
				fillSlot={fillSlot}
				{...drillProps}
			/>
		);
	}

	if (chartKind === PERSISTED_CHART_KIND.PIPELINE_MAP) {
		return (
			<OrderPrimaryPhaseChinaMap
				orders={map.orders}
				geoAggregates={map.geoAggregates}
				highlightProvinceNames={map.highlightProvinceNames}
				highlightAnchorCityRows={map.highlightAnchorCityRows}
				isLoading={map.isLoading}
				animationKey={map.animationKey}
				height={height}
				compact
				fillSlot={fillSlot}
				fitWideSlot
				chartInstanceKeyPrefix={map.chartInstanceKeyPrefix}
				{...drillProps}
			/>
		);
	}

	if (chartKind === PERSISTED_CHART_KIND.OVERDUE) {
		return (
			<DashboardOverdueRankPanel
				overdueData={overdue.data}
				isLoading={overdue.isLoading}
				activeDimension={isSale ? OVERDUE_CHART_DIMENSION.CUSTOMER : overdueDimension}
				onDimensionChange={isSale ? undefined : setOverdueDimension}
				barHeight={height}
				fillSlot={fillSlot}
				lockDimension={overdue.lockDimension}
				{...drillProps}
			/>
		);
	}

	return null;
};

BuiltinLiveChartBody.propTypes = {
	snapshot: PropTypes.object.isRequired,
	height: PropTypes.string,
	fillSlot: PropTypes.bool,
	enableOrderDrill: PropTypes.bool,
	onDrillSelect: PropTypes.func,
	frozenBundle: PropTypes.object,
};

export default memo(BuiltinLiveChartBody);
